// 정수 밀리초 브랜드 타입 — 이 제품의 모든 시간 값이 지나가는 문
//
// 사용자가 코드를 읽지 않으므로 방어를 규율이 아니라 기계에 건다. 이 파일이 그 첫
// 자리다. 규약 둘을 컴파일 에러와 예외로 바꾼다.
//
// 규약 하나 — **모든 시간은 정수 밀리초다.** 초 단위 실수를 그대로 들고 다니면
// 0.1 * 3 이 0.30000000000000004 가 되는 이진 부동소수 오차가 착탄 계산에 그대로
// 스며든다. 이 제품은 1~2초를 다투는 도구라 그 오차가 실제 실패가 된다.
//
// 규약 둘 — **절대 시각과 기간은 다른 타입이다.** 둘 다 그냥 number 면 절대 시각 둘을
// 더하는 코드가 조용히 컴파일된다. 그렇게 나온 값은 아무 의미도 없는 숫자인데 화면에는
// 그럴듯한 시각으로 찍히고, 틀린 주장은 아무 주장도 하지 않는 것보다 나쁘다.

declare const INSTANT: unique symbol
declare const DURATION: unique symbol

/** UTC epoch 로부터의 절대 시각. 정수 밀리초. */
export type Instant = number & { readonly [INSTANT]: 'utc-ms' }

/** 기간. 정수 밀리초이고 음수일 수 있다 — offset 은 음수가 될 수 있기 때문이다. */
export type Duration = number & { readonly [DURATION]: 'ms' }

function assertIntegerMillis(value: number, what: string): void {
  if (!Number.isFinite(value)) {
    throw new RangeError(`${what}: 유한한 수가 아니다 (${String(value)})`)
  }
  if (!Number.isInteger(value)) {
    throw new RangeError(
      `${what}: 정수 밀리초가 아니다 (${String(value)}). 초 단위 실수를 넣으려면 ` +
        `durationFromSeconds 를 쓸 것`,
    )
  }
}

export function instant(millis: number): Instant {
  assertIntegerMillis(millis, '절대 시각')
  return millis as Instant
}

export function duration(millis: number): Duration {
  assertIntegerMillis(millis, '기간')
  return millis as Duration
}

/** 브랜드를 벗겨 원시 밀리초를 꺼낸다. 저장·전송 경계에서만 쓴다. */
export function toMillis(value: Instant | Duration): number {
  return value
}

/**
 * 초 단위 실수를 기간으로 바꾼다. **0.1초 격자 위의 값만 받는다.**
 *
 * 반올림으로 삼키지 않고 거절하는 이유: 삼키면 사람이 화면에 적은 값과 계산에 쓰인
 * 값이 달라지고, 그 차이는 아무도 못 본다. 이 제품에서 조용한 차이는 곧 착탄 순서가
 * 어긋나는 실패다.
 */
export function durationFromSeconds(seconds: number): Duration {
  if (!Number.isFinite(seconds)) {
    throw new RangeError(`기간: 유한한 수가 아니다 (${String(seconds)})`)
  }
  // 0.1 을 곱해 만든 값은 이진 부동소수에서 정확히 떨어지지 않는다. 먼저 10을 곱해
  // 가장 가까운 정수로 맞춘 뒤, 그 정수가 원래 값과 실질적으로 같은지를 확인한다.
  // 이렇게 하지 않으면 8.7 같은 정상 입력이 정수 검사에 걸려 거절된다.
  const tenths = Math.round(seconds * 10)
  if (Math.abs(seconds * 10 - tenths) > 1e-6) {
    throw new RangeError(
      `기간: 0.1초 해상도를 벗어났다 (${String(seconds)}초). 이 제품의 시간 입력은 ` +
        `소수점 한 자리까지다`,
    )
  }
  return (tenths * 100) as Duration
}

export function addDuration(at: Instant, by: Duration): Instant {
  return (at + by) as Instant
}

/** from 에서 to 까지의 기간. to 가 이르면 음수다. */
export function between(from: Instant, to: Instant): Duration {
  return (to - from) as Duration
}

export function plus(a: Duration, b: Duration): Duration {
  return (a + b) as Duration
}

const MMSS = /^(-)?(\d{2,}):([0-5]\d)(?:\.(\d))?$/

/**
 * `mm:ss` 또는 `mm:ss.d` 를 기간으로 읽는다. 앞의 `-` 로 음수를 쓴다.
 *
 * 분을 두 자리 미만으로 쓰는 것(`4:00`)과 초가 60 이상인 것(`04:60`)과 소수점 두 자리
 * (`04:00.55`)를 전부 거절한다. 관대하게 받아 주면 사람이 의도한 값과 시스템이 읽은
 * 값이 갈리는데, 갈린 것을 알려 줄 사람이 이 프로젝트에는 없다.
 */
export function parseMmss(text: string): Duration {
  const m = MMSS.exec(text)
  if (!m) {
    throw new RangeError(`시간 형식이 아니다: ${JSON.stringify(text)} — mm:ss 또는 mm:ss.d`)
  }
  const [, sign, mm, ss, tenth] = m
  const millis =
    (Number(mm) * 60 + Number(ss)) * 1000 + (tenth === undefined ? 0 : Number(tenth) * 100)
  return ((sign === '-' ? -millis : millis) as number) as Duration
}

/** 기간을 `mm:ss.d` 로 적는다. 0.1초 격자 위가 아니면 거절한다. */
export function formatMmss(value: Duration): string {
  const millis = toMillis(value)
  if (millis % 100 !== 0) {
    throw new RangeError(
      `mm:ss 로 적을 수 없다 (${String(millis)}ms) — 이 칸은 0.1초 해상도다`,
    )
  }
  const sign = millis < 0 ? '-' : ''
  const abs = Math.abs(millis)
  const tenth = (abs / 100) % 10
  const totalSeconds = Math.floor(abs / 1000)
  const mm = Math.floor(totalSeconds / 60)
  const ss = totalSeconds % 60
  return `${sign}${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}.${String(tenth)}`
}
